// Strict capture + Daily.co send settings tuned for long-distance mobile calls
export const VIDEO_CONSTRAINTS = {
  width: { ideal: 640, max: 640 },
  height: { ideal: 480, max: 480 },
  frameRate: { ideal: 24, max: 24 },
  facingMode: "user",
};

export const AUDIO_CONSTRAINTS = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  channelCount: 1,
  sampleRate: 48000,
};

// Simulcast layers from a 640x480 source; the top layer is capped at 400kbps
export const SEND_SETTINGS = {
  video: {
    allowAdaptiveLayers: false,
    maxQuality: "high",
    encodings: {
      low: { maxBitrate: 100000, scaleResolutionDownBy: 2, maxFramerate: 15 },
      medium: { maxBitrate: 220000, scaleResolutionDownBy: 1.333, maxFramerate: 20 },
      high: { maxBitrate: 400000, scaleResolutionDownBy: 1, maxFramerate: 24 },
    },
  },
};

export const DAILY_CALL_OPTIONS = {
  subscribeToTracksAutomatically: true,
  inputSettings: {
    video: { settings: VIDEO_CONSTRAINTS },
    audio: { settings: AUDIO_CONSTRAINTS },
  },
  sendSettings: SEND_SETTINGS,
  dailyConfig: {
    micAudioMode: { bitrate: 32000, stereo: false },
    preferH264ForCam: false,
  },
};

export const isMobile = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

// Highest receive layer index: desktop 0-2, mobile 0-1
export const topLayer = () => (isMobile() ? 1 : 2);

// Camera / mic chosen on the Setup Test page (per device, stored locally)
export const DEVICE_KEYS = { cam: "moyo_cam_id", mic: "moyo_mic_id", tested: "moyo_setup_tested" };
export const savedDevice = (k) => localStorage.getItem(DEVICE_KEYS[k]) || null;

export const videoConstraints = () => {
  const cam = savedDevice("cam");
  if (!cam) return VIDEO_CONSTRAINTS;
  const { facingMode, ...rest } = VIDEO_CONSTRAINTS;
  return { ...rest, deviceId: { ideal: cam } };
};

export const audioConstraints = () => {
  const mic = savedDevice("mic");
  return mic ? { ...AUDIO_CONSTRAINTS, deviceId: { ideal: mic } } : AUDIO_CONSTRAINTS;
};

export const dailyCallOptions = () => DAILY_CALL_OPTIONS;

// Switch a joined Daily call to the saved camera/mic, only if those devices still exist
export async function applySavedDevices(call) {
  const cam = savedDevice("cam"), mic = savedDevice("mic");
  if (!cam && !mic) return;
  const ids = new Set((await navigator.mediaDevices.enumerateDevices().catch(() => [])).map((d) => d.deviceId));
  const opts = {};
  if (cam && ids.has(cam)) opts.videoSource = cam;
  if (mic && ids.has(mic)) opts.audioSource = mic;
  if (Object.keys(opts).length) await call.setInputDevicesAsync(opts).catch(() => {});
}

// getUserMedia constraints that use the saved camera/mic exactly when it's still plugged in
export async function savedMediaConstraints() {
  const ids = new Set((await navigator.mediaDevices.enumerateDevices().catch(() => [])).map((d) => d.deviceId));
  const cam = savedDevice("cam"), mic = savedDevice("mic");
  const { facingMode, ...v } = VIDEO_CONSTRAINTS;
  return {
    video: cam && ids.has(cam) ? { ...v, deviceId: { exact: cam } } : VIDEO_CONSTRAINTS,
    audio: mic && ids.has(mic) ? { ...AUDIO_CONSTRAINTS, deviceId: { exact: mic } } : AUDIO_CONSTRAINTS,
  };
}
